import { useEffect } from 'react'
import { useParams, Link, Navigate } from 'react-router-dom'
import SupportLayout from './SupportLayout'
import { getArticleBySlug, getRelatedArticles, CATEGORIES } from './articles'
import usePageTitle from '../../hooks/usePageTitle'

// ─── SupportArticle ──────────────────────────────────────────────────────────
//
// Renders a single article identified by the :slug URL param.
// If the slug doesn't match anything, redirects to /support.

export default function SupportArticle() {
  const { slug } = useParams()
  const article  = getArticleBySlug(slug)
  usePageTitle(article?.title || 'Support')

  // Scroll to top whenever the article changes
  useEffect(() => { window.scrollTo(0, 0) }, [slug])

  if (!article) {
    return <Navigate to="/support" replace />
  }

  const related        = getRelatedArticles(article.slug)
  const categoryLabel  = CATEGORIES.find(c => c.id === article.category)?.label || ''

  return (
    <SupportLayout
      currentSlug={article.slug}
      back={{ label: 'All support articles', to: '/support' }}
      title={article.title}
      subtitle={categoryLabel}
    >
      <article style={{
        background: 'var(--white)',
        border: '1px solid var(--surface-2)',
        borderRadius: 12,
        padding: 'clamp(20px, 4vw, 36px)',
        maxWidth: 760,
      }}>
        {article.body}
      </article>

      {/* Related articles */}
      {related.length > 0 && (
        <section style={{ marginTop: 40, maxWidth: 760 }}>
          <h2 style={{
            margin: '0 0 14px',
            fontSize: '0.85rem',
            fontWeight: 700,
            color: 'var(--ink-muted)',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}>
            Other guides in {categoryLabel}
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: 10,
          }}>
            {related.map(r => (
              <Link
                key={r.slug}
                to={`/support/${r.slug}`}
                style={{
                  display: 'block',
                  padding: '12px 14px',
                  background: 'var(--white)',
                  border: '1px solid var(--surface-2)',
                  borderRadius: 8,
                  textDecoration: 'none',
                  fontSize: '0.9rem',
                  color: 'var(--ink)',
                  fontWeight: 500,
                  lineHeight: 1.4,
                }}
              >
                {r.title}
                {r.draft && (
                  <span style={{
                    fontSize: '0.68rem',
                    color: 'var(--ink-faint)',
                    marginLeft: 6,
                    fontWeight: 400,
                  }}>
                    (coming soon)
                  </span>
                )}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Still stuck CTA */}
      <section style={{
        marginTop: 40,
        padding: '24px 28px',
        background: 'var(--white)',
        border: '1px dashed var(--surface-3)',
        borderRadius: 10,
        maxWidth: 760,
        textAlign: 'center',
      }}>
        <h3 style={{
          margin: '0 0 8px',
          fontSize: '1rem',
          color: 'var(--ink)',
        }}>
          Didn't answer your question?
        </h3>
        <p style={{
          margin: '0 0 16px',
          fontSize: '0.9rem',
          color: 'var(--ink-muted)',
          lineHeight: 1.55,
        }}>
          Send us a message and we'll get back to you shortly.
        </p>
        <Link
          to="/support/contact"
          className="btn btn-primary"
          style={{ padding: '9px 22px', fontSize: '0.9rem' }}
        >
          Contact support →
        </Link>
      </section>
    </SupportLayout>
  )
}
